import { Component, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, Validators, AbstractControl, ValidatorFn, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { NotifierService } from 'angular-notifier';
import { SignUpPageService } from './sign-up-page.service';
import { UserService } from '../../../user.service';

@Component({
  selector: 'app-sign-up-page',
  templateUrl: './sign-up-page.component.html',
  styleUrls: ['./sign-up-page.component.scss']
})
export class SignUpPageComponent implements OnInit, OnDestroy {
  public data: any;
  isSubmitting = false;
  
  // ✨ Variables pour le popup de succès
  showSuccessModal = false;
  userEmail = '';
  countdown = 30; // Compte à rebours pour la redirection (30 secondes)
  countdownInterval: any;
  countdownPaused = false; // Pour mettre en pause si l'utilisateur interagit
  
  regForm: FormGroup = this.formBuilder.group({
    name: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    password: ['', [
      Validators.required,
      Validators.minLength(6),
      this.passwordStrengthValidator()
    ]],
    repeat_password: ['', [Validators.required, Validators.minLength(6)]]
  }, { validators: this.passwordsMatchValidator() });

  constructor(
    private formBuilder: UntypedFormBuilder,
    private router: Router,
    private notifier: NotifierService,
    private content: SignUpPageService,
    private authService: UserService
  ) {}

  ngOnInit(): void {
    if (this.authService.isLoggedIn()) {
      this.router.navigate(['/']);
    }
  }

  ngOnDestroy(): void {
    // Nettoyer l'interval si le component est détruit
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  private passwordsMatchValidator(): ValidatorFn {
    return (group: AbstractControl): { [key: string]: boolean } | null => {
      const password = group.get('password')?.value;
      const repeat = group.get('repeat_password')?.value;
      return password && repeat && password !== repeat ? { passwordsMismatch: true } : null;
    };
  }

  onRegister(): void {
    if (this.isSubmitting) return;

    if (this.regForm.invalid) {
      if (this.regForm.hasError('passwordsMismatch')) {
        this.notifier.notify('error', '❌ Les mots de passe ne correspondent pas.');
      } else {
        this.notifier.notify('error', '❌ Veuillez remplir tous les champs correctement.');
      }
      return;
    }

    this.isSubmitting = true;

    const { name, email, password } = this.regForm.value;
    const user = { name, email, password };

    console.log('🚀 Tentative d\'inscription avec:', { name, email });

    this.content.registerUser(user).subscribe({
      next: (response) => {
        console.log('✅ Réponse complète du serveur:', response);
        
        // Stocker l'email pour l'afficher dans le popup
        this.userEmail = email;
        
        // ✨ Afficher le popup moderne au lieu de la notification
        this.showSuccessModal = true;
        this.isSubmitting = false;
        
        // Démarrer le compte à rebours pour la redirection
        this.startCountdown();
      },
      error: (err) => {
        console.error('❌ Erreur complète:', err);
        console.error('❌ Status:', err.status);
        console.error('❌ Error body:', err.error);
        
        this.isSubmitting = false;
        
        let errorMsg = "❌ Une erreur est survenue lors de la création du compte.";
        
        if (err.status === 400) {
          const errorData = err.error;
          console.log('🔍 Détail erreur 400:', errorData);
          
          if (errorData?.error?.message) {
            const message = errorData.error.message.toLowerCase();
            if (message.includes('email') && message.includes('already taken')) {
              errorMsg = "❌ Cette adresse email est déjà utilisée. Veuillez vous connecter ou utiliser une autre adresse.";
            } else if (message.includes('username') && message.includes('already taken')) {
              errorMsg = "❌ Ce nom d'utilisateur est déjà pris. Veuillez essayer avec un autre nom.";
            } else {
              errorMsg = `❌ ${errorData.error.message}`;
            }
          } else if (errorData?.message) {
            errorMsg = `❌ ${errorData.message}`;
          }
        } else if (err.status === 500) {
          errorMsg = "❌ Erreur serveur. Le compte a peut-être été créé mais l'email de confirmation n'a pas pu être envoyé. Vérifiez la configuration email de Strapi.";
        } else if (err.status === 0 || err.status === undefined) {
          errorMsg = "❌ Impossible de contacter le serveur. Vérifiez votre connexion internet et que Strapi est démarré.";
        }
        
        this.notifier.notify('error', errorMsg);
      }
    });
  }

  // ✨ Démarrer le compte à rebours pour la redirection automatique
  private startCountdown(): void {
    this.countdownPaused = false;
    this.countdownInterval = setInterval(() => {
      if (!this.countdownPaused) {
        this.countdown--;
        if (this.countdown <= 0) {
          this.redirectToSignIn();
        }
      }
    }, 1000);
  }

  // ✨ Mettre en pause le compte à rebours quand l'utilisateur interagit
  pauseCountdown(): void {
    this.countdownPaused = true;
  }

  // ✨ Reprendre le compte à rebours
  resumeCountdown(): void {
    this.countdownPaused = false;
  }

  // ✨ Rediriger vers la page de connexion
  redirectToSignIn(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    this.showSuccessModal = false;
    this.router.navigate(['/sign-in'], { 
      queryParams: { message: 'account-created' }
    });
  }

  // ✨ Fermer le popup manuellement
  closeModal(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    this.showSuccessModal = false;
  }

  // ✨ Rediriger immédiatement vers la connexion
  goToSignIn(): void {
    this.redirectToSignIn();
  }

  // ✨ Rediriger vers l'accueil
  goToHome(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    this.showSuccessModal = false;
    this.router.navigate(['/']);
  }

  private passwordStrengthValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const value = control.value || '';
      const hasUppercase = /[A-Z]/.test(value);
      const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);
      
      if (!hasUppercase || !hasSpecialChar) {
        return { passwordWeak: true };
      }
      return null;
    };
  }
}